import { useMemo } from "react";
import { useDropdownData } from "@/hooks/useDropdownData";
import { useSubsheetStatuses } from "@/hooks/useSubsheetStatuses";
import { ALL_ITEMS_ID } from "@/lib/constants";
import type { DropdownOption } from "@/types/backlog";

/**
 * Resolves the SELECTABLE status list for a given platform (subsheet).
 *
 * - subsheetId null / ALL_ITEMS_ID  -> all statuses (global order_index order).
 * - per-platform config rows exist  -> only those statuses (still in order_index order).
 * - no config rows                  -> all statuses (default-all).
 *
 * This is for selection surfaces only (pickers, filters, board, bulk, new item).
 * Badge/label lookups should keep using the full dropdownData.statuses/allOptions so that an
 * item already set to a now-disabled status still renders its badge.
 */
export const useAvailableStatuses = (subsheetId: string | null): DropdownOption[] => {
  const { data: dropdownData } = useDropdownData();
  const isScoped = !!subsheetId && subsheetId !== ALL_ITEMS_ID;
  const { data: config } = useSubsheetStatuses(isScoped ? subsheetId : null);

  return useMemo(() => {
    const allStatuses = dropdownData?.statuses ?? [];
    if (!isScoped) return allStatuses;
    if (!config || config.length === 0) return allStatuses; // default-all
    const enabled = new Set(config.map((c) => c.status_id));
    return allStatuses.filter((s) => enabled.has(s.id));
  }, [dropdownData?.statuses, config, isScoped]);
};
